import { useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useLocation, useSearchParams } from "react-router-dom";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import SkeletonType2 from "@/app/components/ui/Skeletons/Type2";
import { useOrderManagementStatsAPI } from "@/app/hooks/api_hooks/orders/useOrderStatsAPI";
import { useOrderManagementOrderListAPI } from "@/app/hooks/api_hooks/orders/useOrderListAPI";
import { IOrderManagementFilterBody, IOrderManagementStatsFilterBody } from "@/api_framework/api_modals/Orders";
import { IOrderManagementState, setIsOrderSearchActive } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { PACKING_ALERTS_REFRESH_EVENT } from "@/constants";

const CONFIRMED_TAB_ID = "3";

// Lets the header's packing-alerts hook re-sync its count when Confirmed is opened.
const notifyConfirmedTabOpened = () => {
  window.dispatchEvent(new Event(PACKING_ALERTS_REFRESH_EVENT));
};

export default function StatsComponent({
  active,
  setActive,
  orderType,
  resetPage,
  resetAllFilters,
  clearSearchState,
  isTabClick,
}) {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const isOrderStatsInProgress = useLoader("isQcOrderStatsLoadingInProgress");
  const [params, _setParams] = useSearchParams();
  const location = useLocation();
  const fetchStatsAPI = useOrderManagementStatsAPI();
  const fetchOrderListAPI = useOrderManagementOrderListAPI();
  const dispatch = useDispatch();
  const { orderFilter, statsList, orderModule } = routeState.state as IOrderManagementState;
  // location.key of the navigation whose ?status= we already applied, so a fresh
  // navigation to the same URL (e.g. navbar "Pack ASAP" click) re-applies it.
  const appliedStatusLocationKeyRef = useRef<string | null>(null);
  const statsData = statsList;
  // RBAC: ORDER_CENTRAL_PERSPECTIVE — legacy roles: super_admin, central_ops
  const isCentralRole = useFeatureAccess("ORDER_CENTRAL_PERSPECTIVE");

  const getOrderSource = () => {
    return orderType === "TRANSFER" ? "TO" : "QC";
  };

  const getOrderStatusByType = (status: string) => {
    return orderType === "Rx" ? "rx_pending" : status;
  };

  const getOrderListParams = (): {
    clinic_id: string;
    start_date: string;
    end_date: string;
    status: string;
    module: string;
    for_central?: string;
  } => {
    return {
      clinic_id: orderFilter?.clinic_id,
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
      status: "",
      module: orderModule,
      ...(isCentralRole && { for_central: "true" }),
    };
  };

  const getStatsParams = (): {
    clinic_id: string;
    start_date: string;
    end_date: string;
  } => {
    return {
      clinic_id: orderFilter?.clinic_id,
      start_date: orderFilter?.start_date,
      end_date: orderFilter?.end_date,
    };
  };

  const handleStatusClick = (val: string, status: string, page = 1) => {
    isTabClick.current = true;
    resetPage?.();
    resetAllFilters();
    clearSearchState?.();
    dispatch(setIsOrderSearchActive(false));
    const params = getOrderListParams();
    const statsParams = getStatsParams();
    const requestBody: IOrderManagementFilterBody = {
      clinic_id: params?.clinic_id?.split(","),
      start_date: params?.start_date,
      end_date: params?.end_date,
      order_status: getOrderStatusByType(status),
      order_source: getOrderSource(),
      source: orderModule,
      page_number: page,
      page_size: 20,
    };
    const statsBody: IOrderManagementStatsFilterBody = {
      start_date: statsParams?.start_date,
      end_date: statsParams?.end_date,
      clinic_id: statsParams?.clinic_id?.split(","),
      order_source: getOrderSource(),
    };
    setActive(val);
    fetchOrderListAPI(requestBody);
    fetchStatsAPI(statsBody);
    if (val === CONFIRMED_TAB_ID) notifyConfirmedTabOpened();
  };

  useEffect(() => {
    if (!orderFilter?.clinic_id || !orderFilter?.start_date || !orderModule) return;
    const pageNumber = 1;
    setActive(CONFIRMED_TAB_ID);
    const params = getOrderListParams();
    const statsBody: IOrderManagementStatsFilterBody = {
      start_date: params?.start_date,
      end_date: params?.end_date,
      clinic_id: params?.clinic_id?.split(","),
      order_source: getOrderSource(),
    };
    const requestBody: IOrderManagementFilterBody = {
      clinic_id: params?.clinic_id?.split(","),
      start_date: params?.start_date,
      end_date: params?.end_date,
      order_status: getOrderStatusByType("confirmed"),
      order_source: getOrderSource(),
      source: orderModule,
      page_number: pageNumber,
      page_size: 20,
    };
    fetchOrderListAPI(requestBody);
    fetchStatsAPI(statsBody);
    notifyConfirmedTabOpened();
  }, [orderFilter?.clinic_id, orderFilter?.start_date, orderModule]);

  useEffect(() => {
    const status = params?.get("status");
    if (!status || appliedStatusLocationKeyRef.current === location.key) return;
    if (!orderFilter?.clinic_id || !orderFilter?.start_date) return;
    appliedStatusLocationKeyRef.current = location.key;
    handleStatusClick(status, "confirmed");
  }, [params.get("status"), location.key, orderFilter]);

  const statsCards = [
    { id: "0", label: "Total Orders", valueKey: "total_orders", status: "" },
    { id: "1", label: "Payment Due", valueKey: "payment_due", status: "payment_due" },
    { id: "2", label: "Pending", valueKey: "pending", status: "pending" },
    { id: "3", label: "Confirmed", valueKey: "confirmed", status: "confirmed" },
    { id: "4", label: "Ready for Pickup", valueKey: "ready_for_pickup", status: "ready_for_pickup" },
    { id: "5", label: "Picked", valueKey: "picked", status: "picked" },
    {
      id: "6",
      label: "RTO",
      valueKey: "return_in_transit",
      status: ["return_in_transit", "return_initiated", "undelivered_in_transit"].join(","),
    },
    { id: "7", label: "Delivered", valueKey: "delivered", status: "delivered" },
    { id: "8", label: "Undelivered", valueKey: "undelivered", status: "undelivered" },
    { id: "9", label: "Cancelled", valueKey: "cancelled", status: "cancelled" },
    { id: "10", label: "Returned", valueKey: "returned", status: "returned" },
    { id: "11", label: "Partially Returned", valueKey: "partially_returned", status: "partially_returned" },
  ];

  return (
    <div className="w-full mt-2">
      {!isOrderStatsInProgress ? (
        <div className="">
          <div className="flex w-full gap-2 overflow-x-auto pb-1">
            {statsCards.map((card) => {
              const isActive = active === card.id;
              const statValue = statsData?.[card.valueKey] ?? 0;
              return (
                <button
                  key={card.id}
                  type="button"
                  className={`min-w-[9.75rem] rounded-lg border px-3 py-2 text-left transition-all duration-150
                    ${isActive ? "border-black bg-white" : "border-gray-300 bg-white hover:border-[#B2DDFF] hover:bg-[#F8FBFF]"}`}
                  onClick={() => handleStatusClick(card.id, card.status)}
                >
                  <h3 className={`${isActive ? "text-black" : "text-[#1D2939]"} text-base font-semibold`}>
                    {statValue}
                  </h3>
                  <p
                    className={`${isActive ? "text-black" : "text-[#667085]"} mt-0.5 whitespace-nowrap text-xs font-medium`}
                  >
                    {card.label}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <SkeletonType2 />
      )}
    </div>
  );
}
